import "./AgreementRate.less";
import strings from "./AgreementRate.strings";
import { GuxBadge, GuxIconTooltipBeta } from "genesys-spark-components-react";
import { useStrings } from "features/i18n/useStrings";

export interface AgreementRateProps {
    agreementRate: number; // 0-1
    hasData: boolean;
    level?: "form" | "question"; // Defaults to "form"
}

type BadgeAccent = "success" | "warning" | "error";

const AGREEMENT_RATE_THRESHOLDS = {
    GOOD: 80,
    MODERATE: 60,
} as const;

const getBadgeAccent = (agreementRate: number): BadgeAccent => {
    const percentage = agreementRate * 100;

    if (percentage >= AGREEMENT_RATE_THRESHOLDS.GOOD) {
        return "success";
    } else if (percentage >= AGREEMENT_RATE_THRESHOLDS.MODERATE) {
        return "warning";
    } else {
        return "error";
    }
};

export function AgreementRate({
    agreementRate,
    hasData,
    level = "form",
}: AgreementRateProps) {
    const t = useStrings(strings);
    // Only display if we have data - form validation is handled at Redux selector level
    if (!hasData) {
        return null;
    }

    const badgeAccent = getBadgeAccent(agreementRate);
    const percentage = Math.round(agreementRate * 100);
    const tooltipText =
        level === "question"
            ? t.tooltip.questionAgreementRateInfo
            : t.tooltip.formAgreementRateInfo;

    return (
        <div className="AgreementRate" data-testid="agreement-rate-tag">
            <span>{t.label}</span>
            <GuxIconTooltipBeta icon-name="fa/circle-info-regular">
                <span slot="content" data-testid="agreement-rate-tooltip">
                    {tooltipText}
                </span>
            </GuxIconTooltipBeta>
            :
            <GuxBadge
                bold
                accent={badgeAccent}
                aria-label={t.ariaLabels.agreementRatePercentage({
                    percentage,
                })}
            >
                {t.percentage({ percentage })}
            </GuxBadge>
        </div>
    );
}
